'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface FeaturedStory {
  id: number;
  title: string;
  author: string;
  location: string;
  image: string;
  alt: string;
  quote: string;
  videoUrl?: string;
}

interface FeaturedStoryCarouselProps {
  stories: FeaturedStory[];
}

const FeaturedStoryCarousel: React.FC<FeaturedStoryCarouselProps> = ({ stories }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % stories.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isHydrated, stories.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + stories.length) % stories.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % stories.length);
  };

  if (!isHydrated) {
    return (
      <div className="relative h-[600px] bg-muted rounded-lg overflow-hidden animate-pulse">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const currentStory = stories[currentIndex];

  return (
    <div className="relative h-[600px] bg-card rounded-lg overflow-hidden shadow-lg">
      <div className="absolute inset-0">
        <AppImage
          src={currentStory.image}
          alt={currentStory.alt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/50 to-transparent" />
      </div>

      <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-12">
        <div className="max-w-4xl">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="SparklesIcon" size={24} className="text-accent" />
            <span className="text-accent font-cta text-sm font-bold uppercase tracking-wider">
              Featured Story
            </span>
          </div>

          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-background mb-4 leading-tight">
            {currentStory.title}
          </h2>

          <blockquote className="text-lg lg:text-xl font-quote text-background/90 italic mb-6 leading-relaxed">
            "{currentStory.quote}"
          </blockquote>

          <div className="flex items-center space-x-4 text-background/80 mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="UserIcon" size={20} />
              <span className="font-body text-base">{currentStory.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="MapPinIcon" size={20} />
              <span className="font-body text-base">{currentStory.location}</span>
            </div>
          </div>

          {currentStory.videoUrl && (
            <button className="flex items-center space-x-2 px-6 py-3 bg-accent text-accent-foreground rounded-md font-cta text-base font-bold hover:scale-105 transition-transform duration-300">
              <Icon name="PlayIcon" size={20} />
              <span>Watch Video Story</span>
            </button>
          )}
        </div>
      </div>

      <button
        onClick={handlePrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-background/20 hover:bg-background/40 backdrop-blur-sm rounded-full transition-all duration-300"
        aria-label="Previous story"
      >
        <Icon name="ChevronLeftIcon" size={24} className="text-background" />
      </button>

      <button
        onClick={handleNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-background/20 hover:bg-background/40 backdrop-blur-sm rounded-full transition-all duration-300"
        aria-label="Next story"
      >
        <Icon name="ChevronRightIcon" size={24} className="text-background" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center space-x-2">
        {stories.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-accent w-8' : 'bg-background/50'
            }`}
            aria-label={`Go to story ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default FeaturedStoryCarousel;